import { useState } from "react";
import { taskService } from "@miplatform/api";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IOrderTemplateResponse, ISuccessFailOptions } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { IOrderTemplatePayload } from "../../views/Config/OrderTemplates/ConfigOrderTemplates.types";

const getOrderTemplateDetailsUUID = createUUID();

export const useOrderTemplate = () => {
  const [orderTemplateDetails, setOrderTemplateDetails] =
    useState<IOrderTemplateResponse | null>(null);

  const stateManager = useComponentState();

  const getOrderTemplateDetails = (
    id: string,
    { onSuccess }: ISuccessFailOptions<IOrderTemplateResponse> = {}
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.getById({ id });

      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplateDetails(response.data);
        onSuccess?.(response.data);
      }

      return response;
    }, getOrderTemplateDetailsUUID);
  };

  const updateOrderTemplate = (
    id: string,
    data: Partial<IOrderTemplatePayload>,
    { onSuccess, name }: ISuccessFailOptions<IOrderTemplateResponse> = {}
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.update({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `${name ?? data.name ?? "Order Template"} was updated.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplateDetails(response.data);
        onSuccess?.(response.data);
      }

      return response;
    });
  };

  const deleteOrderTemplate = (
    data: IOrderTemplateResponse,
    { onSuccess }: ISuccessFailOptions<IOrderTemplateResponse> = {}
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.delete({
        id: data.id,
        data: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }

      return response;
    });
  };

  const createOrderTemplate = (
    data: IOrderTemplatePayload,
    { onSuccess }: ISuccessFailOptions<IOrderTemplateResponse> = {}
  ) => {
    const modifiedData = { ...data, type: ASSIGNMENT_TEMPLATE_TYPES.INSTALL }; // TODO: RO Work with BE to remove the need for hardcoding type
    return stateManager.run(async () => {
      const response = await taskService.orderTemplates.create({
        data: modifiedData,
        toastMessageConfig: {
          success: {
            text: `${data.name} was created.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onSuccess?.(response.data);
      }

      return response;
    });
  };

  return {
    orderTemplateDetails,
    setOrderTemplateDetails,
    getOrderTemplateDetails,
    updateOrderTemplate,
    deleteOrderTemplate,
    createOrderTemplate,
    isLoading: stateManager.isBusy(),
  };
};
